import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import { ArrowRight, Target, Mail, Users, FileText, Briefcase, BarChart3, CheckCircle2 } from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const STEPS = [
  { icon: <Target className="w-5 h-5" />, title: 'Strategy & Target List', desc: 'We analyse your asset and build a curated, tiered pharma partner target list with clear rationale for each.' },
  { icon: <FileText className="w-5 h-5" />, title: 'Positioning & Materials', desc: 'We refine your deck, email templates, and non-confidential overview — tailored for each audience segment.' },
  { icon: <Mail className="w-5 h-5" />, title: 'Outreach & Introductions', desc: 'We reach out on your behalf with personalized, evidence-backed messages. Not volume spray — curated engagement.' },
  { icon: <Users className="w-5 h-5" />, title: 'Conference BD Support', desc: 'Before BIO International, BioChina, or any key conference — we agree on priority targets, set up meetings, and prep you. No more wasted trips.' },
  { icon: <Briefcase className="w-5 h-5" />, title: 'Meeting Prep & Follow-Up', desc: 'Concise dossiers for each meeting. Structured follow-up notes and next steps so nothing falls through.' },
  { icon: <BarChart3 className="w-5 h-5" />, title: 'Deal Support', desc: 'As discussions get serious — deal benchmarking, term sheet clarification, and narrative support for boards.' },
];

export default function PartneringPage() {
  useSEO({
    title: 'Biotech Out-Licensing & Pharma Deal Facilitation',
    description: 'Success-based biotech partnering: curated pharma target lists, BD outreach, conference intelligence, CDA facilitation, and deal support. We earn when you close.',
    canonical: 'https://foresightrx.com/partnering',
  });
  usePageTracker('Partnering');

  const [stepsRef, stepsInView] = useInView();
  const [feeRef, feeInView] = useInView();
  const [transRef, transInView] = useInView();

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect">
      <PublicNav />

      {/* Hero */}
      <section className="pt-32 lg:pt-40 pb-20 lg:pb-28 bg-[#0A1128] relative overflow-hidden" data-testid="partnering-hero">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] bg-[#70C24C]/8 rounded-full blur-[120px] pulse-glow" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4 reveal-up">Deal Pathway</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] reveal-up stagger-1" data-testid="partnering-heading">
              Stop spending $50K
              <br />
              on conference cocktails.
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-[#A1A8C3] font-light leading-relaxed max-w-2xl reveal-up stagger-2">
              We travel to 12+ conferences a year, meet the pharma BD teams, and put your asset
              directly in front of the ones who are actively looking. Success-based — we earn when you do.
            </p>
            <Link
              to="/apply"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)] reveal-up stagger-3"
              data-testid="partnering-apply-btn"
            >
              Start a Conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section ref={stepsRef} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="partnering-steps">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`text-center mb-16 ${stepsInView ? 'reveal-up' : 'opacity-0'}`}>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
              Six steps from science to signed deal
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <div key={i} className={`bg-[#0A1128]/60 border border-white/5 rounded-2xl p-6 lg:p-8 hover:border-[#70C24C]/20 transition-all duration-500 ${stepsInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`partnering-step-${i}`}>
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#70C24C]/10 flex items-center justify-center text-[#70C24C]">{s.icon}</div>
                  <span className="text-xs font-bold text-[#687399] uppercase tracking-wider">Step {i + 1}</span>
                </div>
                <h4 className="text-base font-bold text-white mb-2">{s.title}</h4>
                <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Model */}
      <section ref={feeRef} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="fee-model">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className={feeInView ? 'reveal-up' : 'opacity-0'}>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Fee Model</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
                Success-based.
                <br />
                Aligned incentives.
              </h2>
              <p className="text-[#A1A8C3] font-light leading-relaxed mb-6">
                We do not sell meetings or charge per introduction.
                Our fees are structured to align our success with yours.
              </p>
              <p className="text-[#A1A8C3] font-light leading-relaxed">
                We sell <strong className="text-white font-medium">analysis, prioritization, positioning, and representation</strong> — not
                access to a list you could get from a conference badge scan.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { level: 'Level 1 — Strategy & Target List', desc: 'Included in Deal Pathway. Asset analysis, curated target list (20-40 names), tiered by fit and rationale.', tag: 'Included', color: '#70C24C' },
                { level: 'Level 2 — Outreach & Conference Support', desc: 'Proactive introductions, deck refinement, meeting prep, conference BD packages. Fixed add-on fee.', tag: 'Add-on', color: '#00709B' },
                { level: 'Level 3 — Success Fee', desc: 'A percentage of capital raised or deal value when a transaction closes through our introductions.', tag: 'On success', color: '#70C24C' },
              ].map((item, i) => (
                <div key={i} className={`p-6 rounded-2xl bg-[#111A3A] border border-white/5 hover:border-white/10 transition-all duration-300 ${feeInView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`fee-level-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <h4 className="text-sm font-bold text-white">{item.level}</h4>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${item.color}15`, color: item.color }}>{item.tag}</span>
                  </div>
                  <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section ref={transRef} className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="transparency">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-[#00709B]/8 to-[#70C24C]/8 blur-[100px]" />
        <div className={`relative max-w-3xl mx-auto px-6 lg:px-12 text-center ${transInView ? 'reveal-up' : 'opacity-0'}`}>
          <h2 className="text-2xl font-extrabold text-white tracking-tight mb-6">
            Full transparency
          </h2>
          <p className="text-[#A1A8C3] font-light leading-relaxed mb-4">
            We use our global network and intelligence — conference lists, pharma BD databases,
            and AI-powered market research — to identify and prioritize the most relevant pharma
            partners for your asset.
          </p>
          <p className="text-[#A1A8C3] font-light leading-relaxed">
            You receive our <strong className="text-white font-medium">thinking</strong>, not raw data.
            Curated target lists, strategic positioning, and guided access to the right conversations.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#0A1128]" data-testid="partnering-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl font-extrabold text-white tracking-tight mb-6">
            Let us find the right partners for your asset
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10">
            Tell us about your lead asset and objectives. We will come back with an honest view
            of whether we can help and how.
          </p>
          <Link
            to="/apply"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]"
            data-testid="partnering-bottom-cta"
          >
            Apply Now <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
